import { useState } from 'react';
import { Container, Title, Table, Button, Group, Modal, NumberInput, Stack, Badge, Text, ActionIcon } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconTrash, IconRefresh, IconShoppingCart } from '@tabler/icons-react';
import { useParLevels, useSetParLevel, useRemoveParLevel, useCheckBelowPar, useCreateParOrder } from '@/hooks/usePortalApi';

export default function ParLevels() {
  const { data } = useParLevels();
  const checkBelowPar = useCheckBelowPar();
  const setParLevel = useSetParLevel();
  const removeParLevel = useRemoveParLevel();
  const createParOrder = useCreateParOrder();
  const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);
  const [productId, setProductId] = useState<number | ''>('');
  const [parLevel, setParLevelVal] = useState<number | ''>('');
  const [reorderQty, setReorderQty] = useState<number | ''>('');

  const levels = data?.par_levels ?? [];
  const belowPar = checkBelowPar.data?.below_par ?? [];

  const handleAdd = () => {
    if (!productId || !parLevel) return;
    setParLevel.mutate({ product_id: Number(productId), par_level: Number(parLevel), reorder_qty: reorderQty ? Number(reorderQty) : undefined }, {
      onSuccess: () => { closeAdd(); notifications.show({ title: 'Saved', message: 'Par level set', color: 'green' }); },
    });
  };

  const handleCheck = () => {
    checkBelowPar.refetch();
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>Par Levels</Title>
        <Group>
          <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={handleCheck}>Check Stock</Button>
          <Button leftSection={<IconPlus size={16} />} onClick={openAdd}>Add Product</Button>
        </Group>
      </Group>

      <Table striped highlightOnHover mb="lg">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Product</Table.Th>
            <Table.Th>Par Level</Table.Th>
            <Table.Th>Reorder Qty</Table.Th>
            <Table.Th>Price</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {levels.map((l) => (
            <Table.Tr key={l.id}>
              <Table.Td>{l.product_name} <Text size="xs" c="dimmed">{l.product_sku}</Text></Table.Td>
              <Table.Td>{l.par_level}</Table.Td>
              <Table.Td>{l.reorder_qty ?? '-'}</Table.Td>
              <Table.Td>${l.current_price.toFixed(2)}</Table.Td>
              <Table.Td>
                <ActionIcon color="red" variant="subtle" onClick={() => removeParLevel.mutate(l.product_id)}>
                  <IconTrash size={16} />
                </ActionIcon>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {belowPar.length > 0 && (
        <>
          <Title order={3} mb="sm">Below Par Items</Title>
          <Table striped mb="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th>Par Level</Table.Th>
                <Table.Th>Available</Table.Th>
                <Table.Th>Shortage</Table.Th>
                <Table.Th>Suggested Qty</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {belowPar.map((b) => (
                <Table.Tr key={b.product_id}>
                  <Table.Td>{b.product_name}</Table.Td>
                  <Table.Td>{b.par_level}</Table.Td>
                  <Table.Td><Badge color="red">{b.available}</Badge></Table.Td>
                  <Table.Td>{b.shortage}</Table.Td>
                  <Table.Td>{b.suggested_qty}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Button leftSection={<IconShoppingCart size={16} />}
            onClick={() => createParOrder.mutate({ items: belowPar.map(b => ({ product_id: b.product_id, quantity: b.suggested_qty })), delivery_date: new Date().toISOString().split('T')[0] })}
            loading={createParOrder.isPending}>
            Create Order from Below-Par
          </Button>
        </>
      )}

      <Modal opened={addOpened} onClose={closeAdd} title="Add Par Level">
        <Stack>
          <NumberInput label="Product ID" value={productId} onChange={(v) => setProductId(typeof v === 'number' ? v : '')} />
          <NumberInput label="Par Level" value={parLevel} onChange={(v) => setParLevelVal(typeof v === 'number' ? v : '')} />
          <NumberInput label="Reorder Qty (optional)" value={reorderQty} onChange={(v) => setReorderQty(typeof v === 'number' ? v : '')} />
          <Button onClick={handleAdd} loading={setParLevel.isPending}>Save</Button>
        </Stack>
      </Modal>
    </Container>
  );
}
